import { useEffect, useMemo, useState } from 'react';
import { apiMaybe, apiRequest } from '../../services/apiClient';
import { daysBetween } from '../../utils/format';

export function getDashboardGreeting() {
  const hour = new Date().getHours();
  if (hour < 5) return 'Working late';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// byType arrives as a Postgres GROUP BY with NO ORDER BY (routes/dashboard.js), so
// equal counts sit in arbitrary order. That was invisible while the card listed every
// type; now that the card cuts the list at PORTFOLIO_TYPE_ROW_CAP (doc-237), two types
// with the same count could swap across the cut line between reloads and a type would
// appear and disappear for no reason the reader can see. The name tie-break is what
// makes the cut explainable, not a nicety.
//
// max is the FULL list's max, not the visible slice's, so the bar scale does not move
// when the reader expands the card.
export function buildTypeHeat(byType) {
  const entries = Object.entries(byType)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const max = entries[0]?.[1] || 1;
  return { entries, max };
}

export default function useDashboardData({ canWriteContracts }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [dismissingId, setDismissingId] = useState(null);

  useEffect(() => {
    apiRequest('/dashboard')
      .then((data) => {
        setDashboard(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!canWriteContracts) return;
    // apiMaybe resolves null on failure (and logs it) — the feed just stays empty.
    apiMaybe('/activity/recent?limit=12&days=14')
      .then((data) => setRecentActivity(data?.events || []));
  }, [canWriteContracts]);

  const dismissReminder = async (contractId, kind = 'renewal') => {
    if (!canWriteContracts) return;
    setDismissingId(contractId);
    try {
      await apiRequest(`/contracts/${contractId}/dismiss-${kind}`, {
        method: 'POST',
        json: {},
      });
      setDashboard(await apiRequest('/dashboard'));
    } finally {
      setDismissingId(null);
    }
  };

  const totalContracts = dashboard?.totalContracts || 0;
  const byStatus = dashboard?.byStatus || {};
  const activeCount = byStatus.active || 0;
  const draftCount = byStatus.draft || 0;
  const inReviewCount = byStatus.in_review || 0;
  const expiring30 = dashboard?.expiring?.in30Days || 0;
  const noticeUpcoming30 = dashboard?.noticeUpcoming?.in30Days || 0;
  const deadlines30 = dashboard?.deadlinesUpcoming?.in30Days || 0;
  const expiringSoon = useMemo(() => dashboard?.expiringSoon || [], [dashboard]);
  const noticeSoon = useMemo(() => dashboard?.noticeSoon || [], [dashboard]);
  const recent = dashboard?.recentlyUpdated || [];
  const byType = useMemo(() => dashboard?.byType || {}, [dashboard]);
  // Absent from the payload for anyone without write_contracts (the backend
  // omits both blocks) — left undefined here so the tiles simply don't render.
  const reviewQueue = dashboard?.reviewQueue;
  const tprmPipeline = dashboard?.tprmPipeline;

  const topDeadline = useMemo(() => {
    const candidates = [
      ...expiringSoon.map((contract) => ({
        kind: 'expiring',
        contract,
        date: contract.expirationDate,
        days: daysBetween(new Date(), contract.expirationDate),
      })),
      ...noticeSoon.map((contract) => ({
        kind: 'notice',
        contract,
        date: contract.autoRenewalNoticeDate,
        days: daysBetween(new Date(), contract.autoRenewalNoticeDate),
      })),
    ].filter((item) => item.days != null && item.days <= 45);
    if (!candidates.length) return null;
    candidates.sort((a, b) => a.days - b.days);
    return candidates[0];
  }, [expiringSoon, noticeSoon]);

  // Every entry, never a slice: the card's footer has to print the true total and its
  // expander has to have real rows to reveal (doc-237).
  const typeHeat = useMemo(() => buildTypeHeat(byType), [byType]);

  return {
    activeCount,
    dashboard,
    deadlines30,
    dismissReminder,
    dismissingId,
    draftCount,
    error,
    expiring30,
    expiringSoon,
    inReviewCount,
    loading,
    noticeSoon,
    noticeUpcoming30,
    recent,
    recentActivity,
    reviewQueue,
    topDeadline,
    totalContracts,
    tprmPipeline,
    typeHeat,
  };
}
